<template>
  <Dropdown class="filter-dropdown">
    <template #button-content>
      <span class="filter-dropdown--label">{{
        `${label}: ${selectedValuesLabel}`
      }}</span
      ><span class="icon-arrow"></span
    ></template>

    <!-- LISTAGEM DE OPÇÕES -->
    <div class="filter-dropdown--options">
      <SearchInput
        v-if="hasSearch"
        v-model="searchTerm"
        :placeholder="searchFieldPlaceholder"
        :fieldsToSearch="fieldsToSearch"
        class="filter-dropdown--search-field"
      />

      <slot name="options" :bind="options">
        <!-- RADIO -->
        <FilterDropdownRadio
          v-if="radioOptions"
          v-model="selectedValues"
          :options="filteredOption"
          @input="$emit('input', $event)"
        />
        <!-- CHECKBOX -->
        <FilterDropdownCheckbox
          v-else
          v-model="selectedValues"
          :options="filteredOption"
          :searchTerm="searchTerm"
          @input="$emit('input', $event)"
        />
      </slot>
    </div>
  </Dropdown>
</template>

<script>
import FilterDropdownRadio from './FilterDropdownRadio'
import FilterDropdownCheckbox from './FilterDropdownCheckbox'
import SearchInput from '../elements/SearchInput'
import Dropdown from '@/components/_base/patterns/Dropdown'

export default {
  name: 'FilterDropdown',
  components: {
    FilterDropdownRadio,
    FilterDropdownCheckbox,
    SearchInput,
    Dropdown
  },
  props: {
    /**
     * Valor do filtro
     */
    value: {
      type: [Array, Object, Number, String]
    },
    /**
     * Label do nome do select
     */
    label: {
      type: String,
      default: 'Status'
    },
    /**
     * Array de objetos das opções
     * Obj exemplo:
     * {
     *  label: 'Nome da opção',
     *  value: 0
     * }
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Define se as opções devem ser do tipo radio
     * @default {Boolean} false
     */
    radioOptions: {
      type: Boolean,
      default: true
    },
    /**
     * Se deve mostrar campo de busca
     * @default {Boolean} false
     */
    hasSearch: {
      type: Boolean,
      default: false
    },
    /**
     * Placeholder do campo de busca
     * @default {String} Buscar
     */
    searchFieldPlaceholder: {
      type: String,
      default: 'Buscar'
    },
    /**
     * Campos do array de opções que devem ser procurados
     * @default {String} label
     */
    fieldsToSearch: {
      type: [Array], // array de strings com os nomes das propriesdades a serem pesquisadas
      default: () => ['label']
    },
    /**
     * Texto que deve aparecer quando não houver opções selecionadas
     * @default {String} Todos
     */
    checkboxNoSelectionLabel: {
      type: String,
      default: 'Todos'
    },
    /**
     * Se deve apresentar uma opção para selecionar todas as opções
     * Somente para tipo checkbox
     */
    toggleAll: {
      type: Boolean,
      default: true
    },
    /**
     * Texto da opção de seleção de todas
     * Somente para tipo checkbox
     */
    toggleAllLabel: {
      type: String,
      default: 'Todas'
    }
  },
  data() {
    return {
      selectedValues: [],
      searchTerm: '',
      hasGroups: false
    }
  },
  computed: {
    selectedValuesLabel() {
      /** Se todas as opções estão selecionadas retorna a prop checkboxNoSelectionLabel */
      if (
        Array.isArray(this.selectedValues) &&
        this.selectedValues.length ===
          this.options.flatMap((item) => item.subfields).length
      ) {
        return this.checkboxNoSelectionLabel
      }

      /**
       * Retorna os nomes do itens selecionados concatenados
       * ou o valor da prop checkboxNoSelectionLabel se não houver valor selecionado
       * */
      return Array.isArray(this.selectedValues)
        ? this.selectedValues.map((item) => item.label).join(', ') ||
            this.checkboxNoSelectionLabel
        : this.selectedValues?.label || ''
    },
    filteredOption() {
      if (this.searchTerm.trim() !== '' && this.radioOptions) {
        return this.filterOptions()
      }

      return this.options
    }
  },
  watch: {
    value(newValue) {
      this.selectedValues = newValue
    }
  },
  created() {
    this.selectedValues = this.value
  },
  methods: {
    /**
     * Filtra as opções de acordo com o termo buscado
     */
    filterOptions(subfields = null) {
      const rgx = new RegExp(this.searchTerm, 'gim')
      const contentToSearch = subfields || this.options

      return contentToSearch.filter((item) => {
        let match = false

        this.fieldsToSearch.forEach((f) => {
          if (rgx.test(item[f])) {
            match = true
          }
        })

        return match
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-dropdown {
  max-width: 185px;
  min-width: 125px;
  border: 1px solid $gray-border;

  &--label {
    display: inline-block;
    width: calc(100% - 10px);
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: font-size('size-small');
    text-align: left;
    white-space: nowrap;
  }

  .icon-arrow {
    @include arrow(bottom, $gray-darkest, 5px, false);
    margin-left: 5px;
  }

  & ::v-deep .dropdown--trigger-button {
    display: flex;
    width: 100%;
    padding: 10px 16px;
    justify-content: space-between;
    align-items: center;
    border-radius: 0;
    color: $gray-darkest;
    border: none;
    background-color: $white;

    &:after {
      border-top-color: $gray-darkest;
    }

    &:active,
    &[aria-expanded='true'] {
      color: $white;
      border-color: $blue;
      background-color: $blue;

      .icon-arrow {
        border-top-color: $white;
      }
    }
  }

  & ::v-deep .dropdown--options {
    min-width: 204px;
    max-height: 285px;
    overflow-x: auto;
    border-radius: 0;
    border-color: $gray-border;
    @include drop-shadow(0px 2px 15px rgba(0, 0, 0, 0.15));
  }

  &--options {
    padding: 5px 15px;
  }

  &--search-field {
    margin-bottom: 25px;
  }
}
</style>
